import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Clock, PenLine, Trash2, Image as ImageIcon } from "lucide-react";
import { API_BASE } from "./imageUtils";

const ICON = {
  pending: <Clock className="h-4 w-4 text-slate-400" />,
  processing: <Loader2 className="h-4 w-4 animate-spin text-sky-600" />,
  done: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  failed: <XCircle className="h-4 w-4 text-red-600" />,
  manual: <PenLine className="h-4 w-4 text-slate-600" />,
};
const TEXT = { pending: "Waiting in queue", processing: "Reading + double-checking now…", manual: "Lines typed manually" };
const since = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000)) : 0);

const PageRow = ({ p, locked, onChange }) => {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <li data-testid={`page-status-${p.id}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm">
      {ICON[p.status]}
      <span className="font-medium">{p.plant} · Page {p.page_no}</span>
      <span className="text-slate-400 truncate max-w-40">{p.name}</span>
      <span className="text-slate-600">{p.status === "done" ? `${p.line_count} line(s) read${p.read_seconds ? ` in ${Math.round(p.read_seconds)}s` : ""}` : p.status === "processing" ? `${TEXT.processing} ${since(p.started_at)}s` : TEXT[p.status]}</span>
      {p.error && <span className="text-red-700 w-full sm:w-auto">{p.error}</span>}
      {p.page_issues?.length > 0 && <span className="text-xs text-amber-700 w-full">Unreadable on page: {p.page_issues.join("; ")}</span>}
      {!locked && (
        <span className="flex flex-wrap gap-1 sm:ml-auto">
          {p.has_image && <a href={`${API_BASE}/verification/pages/${p.id}/image`} target="_blank" rel="noreferrer" data-testid={`page-photo-${p.id}`}
            className="inline-flex items-center h-9 px-3 rounded-md border text-xs hover:bg-slate-50"><ImageIcon className="h-3 w-3 mr-1" />Photo</a>}
          {p.status === "failed" && <>
            <Button size="sm" variant="outline" className="h-9" data-testid={`page-retry-${p.id}`} onClick={() => act(() => api.post(`/verification/pages/${p.id}/retry`), "Page queued again")}>Retry this page</Button>
            <Button size="sm" variant="ghost" className="h-9" data-testid={`page-manual-${p.id}`} onClick={() => act(() => api.post(`/verification/pages/${p.id}/manual`), "Marked as typed manually")}>I'll type it myself</Button>
          </>}
          {p.status !== "processing" && <Button size="sm" variant="ghost" className="h-9 text-red-600" data-testid={`page-delete-${p.id}`}
            onClick={() => window.confirm(`Remove ${p.plant} page ${p.page_no} and its read lines from this verification?`) && act(() => api.delete(`/verification/pages/${p.id}`), "Page removed")}><Trash2 className="h-3 w-3" /></Button>}
        </span>
      )}
    </li>
  );
};

const PlantProgress = ({ plant, pages }) => {
  const n = (st) => pages.filter((p) => st.includes(p.status)).length;
  const done = n(["done", "manual"]), proc = n(["processing"]), wait = n(["pending"]), fail = n(["failed"]);
  return (
    <div data-testid={`progress-${plant.replace(/\s+/g, "-").toLowerCase()}`} className="space-y-1">
      <div className="flex flex-wrap gap-x-3 text-sm"><b>{plant}</b>
        <span className="text-emerald-700">Completed {done}</span><span className="text-sky-700">Processing {proc}</span>
        <span className="text-slate-600">Remaining {wait}</span>{fail > 0 && <span className="text-red-700">Failed {fail}</span>}
        <span className="text-slate-400">of {pages.length}</span></div>
      <Progress value={(done / pages.length) * 100} />
    </div>
  );
};

export const PagesPanel = ({ pages, locked, onChange }) => {
  if (!pages.length) return null;
  const done = pages.filter((p) => ["done", "manual"].includes(p.status)).length;
  const plants = [...new Set(pages.map((p) => p.plant))];
  const current = pages.filter((p) => p.status === "processing").map((p) => `${p.plant} page ${p.page_no}`);
  return (
    <section data-testid="pages-panel" className="bg-white border rounded-xl p-4 space-y-2">
      <div className="flex flex-wrap justify-between gap-2 text-sm font-semibold">
        <span>Pages processed: <span data-testid="pages-done-count">{done}</span> / {pages.length}</span>
        {current.length > 0 && <span data-testid="pages-current" className="text-sky-700 font-normal">Now reading: {current.join(", ")}</span>}
      </div>
      {plants.map((pl) => <PlantProgress key={pl} plant={pl} pages={pages.filter((p) => p.plant === pl)} />)}
      <ul className="divide-y">{pages.map((p) => <PageRow key={p.id} p={p} locked={locked} onChange={onChange} />)}</ul>
    </section>
  );
};
